// Live balances (Pro): a game's account balances, updated the moment money
// moves, with a feed of the latest changes.
import { useEffect, useState } from "react";
import { UpgradePanel, hasFeature, useMe } from "../../components/Upgrade";
import { Button, Empty, ErrorNotice, Input, Loading, Money, Panel, Segmented } from "../../components/ui";
import type { LiveQuery } from "../../lib/api";
import { useLiveBalances, type LiveRow, type LiveStatus } from "../../lib/live";
import { formatMoney } from "../../lib/money";
import { invalidate } from "../../lib/query";
import { useClient } from "../../lib/session";
import { relativeTime } from "../../lib/time";
import type { GameTabProps } from "./Game";

type Owner = NonNullable<LiveQuery["owner"]>;

const STATUS: Record<LiveStatus, string> = {
  connecting: "Connecting…",
  live: "Live",
  reconnecting: "Reconnecting…",
  paused: "Paused",
  locked: "Pro feature",
  error: "Stopped",
};

/** Re-renders every few seconds so "2 s ago" keeps moving. */
function useTick(ms: number) {
  const [, set] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => set((n) => n + 1), ms);
    return () => window.clearInterval(id);
  }, [ms]);
}

/** The search box, applied once typing pauses. */
function useDebounced(value: string, ms: number) {
  const [v, set] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => set(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function GameLiveBalances({ game, env }: GameTabProps) {
  const me = useMe();
  if (!me.data) return <>{me.loading ? <Loading /> : <ErrorNotice error={me.error} onRetry={me.reload} />}</>;
  if (!hasFeature(me.data, "live_balances")) {
    return (
      <UpgradePanel me={me.data} title="Watch balances move as players play" preview={<Preview />}>
        <p>
          Live balances shows every player and bank account in {game.name} updating the moment money moves: salaries landing,
          shop payments, transfers and card holds. You see it here without refreshing, in sandbox and live.
        </p>
      </UpgradePanel>
    );
  }
  return <Live game={game} env={env} />;
}

function Live({ game, env }: GameTabProps) {
  const client = useClient();
  const [owner, setOwner] = useState<Owner>("player");
  const [search, setSearch] = useState("");
  const [paused, setPaused] = useState(false);
  const q = useDebounced(search.trim(), 300);
  const live = useLiveBalances(client, game.id, { environment: env, owner, q: q || undefined, limit: 50 }, paused);
  useTick(5_000);

  useEffect(() => {
    if (live.status === "locked") invalidate("dev:me");
  }, [live.status]);

  return (
    <div className="stack">
      <div className="live-bar">
        <span className={`live-status live-${live.status}`} role="status">
          <span className="live-dot" aria-hidden="true" />
          {STATUS[live.status]}
        </span>
        <Segmented<Owner> label="Accounts" value={owner} onChange={setOwner}
          options={[{ value: "player", label: "Players" }, { value: "merchant", label: "Shops" }, { value: "system", label: "Bank" }, { value: "all", label: "All" }]} />
        <Input className="grow" type="search" aria-label="Search accounts" placeholder="Account number, name or player ID"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button onClick={() => setPaused((p) => !p)}>{paused ? "Resume" : "Pause"}</Button>
      </div>
      {live.status !== "locked" && <ErrorNotice error={live.status === "reconnecting" || live.status === "error" ? live.error : null} />}

      <div className="live-grid">
        <Panel flush title={`${env === "production" ? "Live" : "Sandbox"} accounts`}
          sub={live.total > live.rows.length ? `The ${live.rows.length} most recently active of ${live.total}.` : "Most recently active first. New activity appears on top."}>
          {live.status === "connecting" && live.rows.length === 0 ? (
            <Loading />
          ) : live.rows.length === 0 ? (
            <Empty title={q ? "No accounts match" : "No accounts yet"}>
              {q ? "Try another account number, name or player ID." : `Accounts appear here as soon as your game opens them in ${env}.`}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="data live-table">
                <thead>
                  <tr><th>Account</th><th className="num">Available</th><th className="num">Held</th><th className="num">Ledger balance</th><th>Last change</th></tr>
                </thead>
                <tbody>
                  {live.rows.map((r) => <Row key={r.account_id} r={r} />)}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel flush title="Latest changes" sub="Since you opened this page.">
          {live.changes.length === 0 ? (
            <p className="muted small panel-body">Waiting for money to move. Try a payment from the Sandbox playground in another tab.</p>
          ) : (
            <ul className="live-feed">
              {live.changes.map((c) => (
                <li key={c.id}>
                  <div className="grow">
                    <div className="cell-title">{c.row.player_name ?? c.row.account_name}</div>
                    <div className="cell-sub">{c.row.account_number} · {relativeTime(new Date(c.at).toISOString())}</div>
                  </div>
                  <div className="num">
                    {c.delta !== 0
                      ? <span className={`live-delta ${c.delta > 0 ? "up" : "down"}`}>{formatMoney(c.delta, c.row.currency, { sign: true })}</span>
                      : <span className="muted small">{c.opened ? "Account opened" : c.row.held_balance ? `${formatMoney(c.row.held_balance, c.row.currency)} held` : "Updated"}</span>}
                    <div className="cell-sub">now {formatMoney(c.row.available_balance, c.row.currency)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Row({ r }: { r: LiveRow }) {
  const tone = r.delta ? (r.delta > 0 ? "up" : "down") : r.changedAt ? "changed" : "";
  return (
    <tr className={r.status !== "active" ? "muted" : undefined}>
      <td>
        <div className="cell-title">{r.player_name ?? r.account_name}</div>
        <div className="cell-sub">
          {r.account_number} · {r.bank_name}
          {r.external_player_id && <> · {r.external_player_id}</>}
          {r.system_role && <> · {r.system_role.replace(/_/g, " ")}</>}
          {r.account_type !== "current" && <> · {r.account_type.replace(/_/g, " ")}</>}
          {r.status !== "active" && <> · {r.status}</>}
        </div>
      </td>
      <td className="num">
        {/* Keyed by version so the highlight replays on every change. */}
        <span key={r.version} className={tone ? `live-flash ${tone}` : undefined}>
          <Money minor={r.available_balance} currency={r.currency} />
        </span>
        {r.delta ? <div className={`live-delta ${r.delta > 0 ? "up" : "down"}`}>{formatMoney(r.delta, r.currency, { sign: true })}</div> : null}
      </td>
      <td className="num">{r.held_balance ? <Money minor={r.held_balance} currency={r.currency} /> : <span className="muted">—</span>}</td>
      <td className="num"><Money minor={r.posted_balance} currency={r.currency} /></td>
      <td>{relativeTime(r.updated_at)}</td>
    </tr>
  );
}

/** A still of the feature for the upgrade prompt. */
function Preview() {
  const ngn = { code: "NGN", exponent: 2, symbol: "₦" };
  const rows = [
    ["Ada Obi", "8204417751", 1_250_000_00, 4_500_00],
    ["Tunde Bello", "8204419032", 312_400_00, -2_000_00],
    ["Chioma Eze", "8204410086", 88_150_00, 15_000_00],
    ["Bukka Hut", "8204415520", 2_904_000_00, 0],
  ] as const;
  return (
    <table className="data live-table">
      <thead><tr><th>Account</th><th className="num">Available</th><th>Last change</th></tr></thead>
      <tbody>
        {rows.map(([name, no, bal, d]) => (
          <tr key={no}>
            <td><div className="cell-title">{name}</div><div className="cell-sub">{no} · Eko Bank</div></td>
            <td className="num">
              <Money minor={bal} currency={ngn} />
              {d !== 0 && <div className={`live-delta ${d > 0 ? "up" : "down"}`}>{formatMoney(d, ngn, { sign: true })}</div>}
            </td>
            <td>just now</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
